import Cookies from 'js-cookie';
import type { AuthLoginData } from './api/types/auth';

/**
 * 인증 쿠키 유틸리티
 *
 * JWT 토큰(access_token)은 httpOnly 쿠키로 서버사이드에서만 설정/삭제됩니다.
 * 클라이언트에서는 토큰 값에 직접 접근할 수 없으며, 대신 아래 헬퍼를 사용합니다.
 *
 * - setAuthToken / removeAuthToken : 사용 금지 (서버 route가 담당)
 * - hasAuthToken                   : auth_status 쿠키로 인증 여부만 확인
 * - getAuthUser                    : auth_user 쿠키에서 사용자 메타데이터 읽기
 */

/** 인증 여부 확인 (auth_status 쿠키 존재 여부) */
export const hasAuthToken = (): boolean => {
  return Cookies.get('auth_status') === '1';
};

/** 사용자 메타데이터 쿠키 읽기 (토큰 미포함) */
export const getAuthUser = (): Omit<AuthLoginData, 'token'> | null => {
  const raw = Cookies.get('auth_user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Omit<AuthLoginData, 'token'>;
  } catch {
    return null;
  }
};

/**
 * @deprecated httpOnly 전환 이후 사용 불가.
 * JWT 값은 JS에서 접근할 수 없습니다.
 * 인증 여부 확인은 hasAuthToken()을 사용하세요.
 */
export const getAuthToken = (): string | undefined => {
  // httpOnly 쿠키이므로 undefined 반환 (하위 호환)
  return undefined;
};

/**
 * @deprecated 서버사이드 /api/auth/login route가 담당합니다.
 */
export const setAuthToken = (_token: string): void => {
  // no-op: httpOnly 쿠키는 클라이언트에서 설정 불가
};

/**
 * @deprecated 서버사이드 /api/auth/logout route가 담당합니다.
 * logout() 함수를 사용하세요.
 */
export const removeAuthToken = (): void => {
  // no-op: httpOnly 쿠키는 클라이언트에서 삭제 불가
};

/**
 * 로그아웃 — /api/auth/logout 호출로 httpOnly 쿠키 일괄 삭제
 */
export const clearAuthCookies = async (): Promise<void> => {
  await fetch('/api/auth/logout', { method: 'POST' });
};
